import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Building2, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NavTop from "@/components/nav/nav_top";
import ButtonMain from "@/components/buttons/button_main";
import ButtonGhost from "@/components/buttons/button_ghost";

// A realtor opens the shareable invite an owner sent them. They preview the
// listing, then accept — which transfers the listing to them to manage. The
// owner stays on as the view-only property owner who signs the lease.
export default function RealtorInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [invite, setInvite] = useState(null); // null=loading, false=not found
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let alive = true;
    api
      .get(`/listings/invites/${token}`)
      .then(({ data }) => alive && setInvite(data))
      .catch(() => alive && setInvite(false));
    return () => { alive = false; };
  }, [token]);

  const isRealtor = user?.lister_kind === "realtor";

  async function accept() {
    if (!user) {
      navigate("/auth/login", { state: { from: `/invite/realtor/${token}` } });
      return;
    }
    setAccepting(true);
    try {
      await api.post(`/listings/invites/${token}/accept`);
      toast.success("Listing accepted. It's now in your dashboard to manage.");
      navigate("/lister");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAccepting(false);
    }
  }

  return (
    <div className="min-h-screen bg-slate-50/70">
      <NavTop />
      <main className="mx-auto max-w-lg px-4 pt-12 sm:px-6">
        {invite === null ? (
          <div className="mt-16 flex justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-brand-lime" />
          </div>
        ) : invite === false ? (
          <div className="rounded-3xl border border-slate-100 bg-white p-8 text-center shadow-sm">
            <h1 className="text-xl font-extrabold text-brand-ink">This invite link isn't valid</h1>
            <p className="mt-2 text-sm text-slate-500">It may have been used already or the link is incomplete.</p>
            <ButtonGhost className="mt-5" onClick={() => navigate("/")}>Go home</ButtonGhost>
          </div>
        ) : (
          <div className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand-tint text-brand-lime">
              <Building2 size={24} />
            </span>
            <h1 className="mt-4 text-2xl font-extrabold tracking-tight text-brand-ink">
              {invite.owner_name || "An owner"} invited you to manage a listing
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              Accept to take over managing this home as the realtor. The owner stays on as the property owner and signs the lease.
            </p>

            {invite.listing && (
              <div className="mt-5 flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
                {invite.listing.photo ? (
                  <img src={invite.listing.photo} alt={invite.listing.title} className="h-16 w-16 shrink-0 rounded-xl object-cover" />
                ) : (
                  <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-xl bg-white text-slate-300">
                    <Building2 size={22} />
                  </span>
                )}
                <div className="min-w-0">
                  <p className="truncate font-bold text-brand-ink">{invite.listing.title || "Listing"}</p>
                  <p className="text-sm text-slate-500">{invite.listing.city}</p>
                </div>
              </div>
            )}

            {invite.status !== "pending" ? (
              <p className="mt-5 rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 ring-1 ring-amber-200/60">
                This invite has already been {invite.status}.
              </p>
            ) : !user ? (
              <div className="mt-6">
                <ButtonMain full onClick={accept}>Log in as a realtor to accept</ButtonMain>
                <p className="mt-2 text-center text-xs text-slate-400">You'll come right back here after signing in.</p>
              </div>
            ) : !isRealtor ? (
              <p className="mt-5 rounded-xl bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-700 ring-1 ring-amber-200/60">
                Only a realtor account can accept a listing assignment. Sign up as a realtor to continue.
              </p>
            ) : (
              <ButtonMain full className="mt-6" onClick={accept} disabled={accepting}>
                <span className="inline-flex items-center gap-2">
                  {accepting ? "Accepting…" : "Accept and manage this listing"} <ArrowRight size={16} />
                </span>
              </ButtonMain>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
